import {
  useCreateWorkflow,
  useGetWorkflowDetails,
  useUpdateWorkflow,
} from '@automata/api/apiComponents';
import { WorkflowDetailed, WorkflowPayload } from '@automata/api/apiSchemas';
import { Typography } from '@automata/mui-theme';
import { TextField, TrackedButton } from '@automata/ui';
import { env, getErrorMessage } from '@automata/utils';
import { dateValueFormatter } from '@automata/utils/time';
import Box from '@mui/system/Box';
import Stack from '@mui/system/Stack';
import router from 'next/router';

import { AsyncStatus } from 'components/AsyncStatus';
import { Placeholder } from 'components/Placeholder';
import { GlobalRoutes } from 'GlobalRoutes';
import { useRootUrl } from 'hooks/useRootUrl';
import { useSnackbar } from 'hooks/useSnackbar';
import { useWorkspaceID } from 'hooks/useWorkspaceID';
import { WorkflowTypes } from 'model/types';
import { getParametersDefinitionsFromCachedWorkflow } from 'utils/helpers';

interface FormWrapperProps {
  workflow: WorkflowDetailed;
  onClose: () => void;
  refetchData: () => Promise<void>;
}

const FormWrapper = ({ workflow, onClose, refetchData }: FormWrapperProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { workspaceID } = useRootUrl();
  const currentWorkspaceID = useWorkspaceID();
  const {
    mutateAsync: createWorkflow,
    isLoading: isUpdating,
    error,
  } = useCreateWorkflow({
    onSuccess: refetchData,
  });
  const { mutateAsync: updateWorkflow, isLoading: isUpdatingWorkflow } =
    useUpdateWorkflow({
      onSuccess: refetchData,
    });
  const workflowInfo = workflow.definition?.api;
  const workflowExportName =
    workflow.name + ` (export ${dateValueFormatter(new Date(), true)})`;
  const parameterDefinitions =
    getParametersDefinitionsFromCachedWorkflow(workflow);

  const handleWorkflowExport = async () => {
    if (currentWorkspaceID === undefined || workspaceID === undefined) {
      return;
    }
    try {
      const response = await createWorkflow({
        body: {
          name: workflowExportName,
          type: WorkflowTypes.MAESTRO,
        },
        pathParams: {
          workspaceID: currentWorkspaceID,
        },
      });

      if (response) {
        handleWorkflowUpdate(response.id, workspaceID);
      }
    } catch (err: unknown) {
      const error = getErrorMessage(err, 'unknown error');
      enqueueSnackbar(`Cannot create workflow: ${error}`, {
        variant: 'error',
      });
    }
  };

  const handleWorkflowUpdate = async (
    workflowID: string,
    workspaceID: string
  ) => {
    const payload: WorkflowPayload = {
      name: workflowExportName,
      definition: {
        maestro: workflow.definition.api?.cached_maestro_workflow,
      },
      attributes: workflow.attributes,
      run_instructions: workflow.run_instructions,
    };
    try {
      await updateWorkflow({
        body: {
          payload,
          reason: {
            details: env('NEXT_PUBLIC_REASON_DEFAULT_STRING'),
          },
        },
        pathParams: {
          workflowID,
        },
      });

      enqueueSnackbar('Workflow exported', { variant: 'success' });
      onClose();
      router.push(
        `${GlobalRoutes.workflow.replace(':org', workspaceID)}/${workflowID}`
      );
    } catch (err: unknown) {
      const error = getErrorMessage(err, 'unknown error');
      enqueueSnackbar(`Cannot update workflow: ${error}`, {
        variant: 'error',
      });
    }
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Stack spacing={2}>
          <TextField
            margin="dense"
            label="Workflow Name"
            value={workflow.name}
            fullWidth
            disabled
          />
        </Stack>
        <Stack spacing={2} pt={4}>
          <Typography variant="overline">
            Parameter And Batching Configuration
          </Typography>
          <Typography variant="h6">Parameters</Typography>
          <Stack>
            {workflowInfo?.parameter_values?.length ? (
              workflowInfo.parameter_values.map((param) => {
                const parameterDefinition = parameterDefinitions?.find(
                  (p) => p.id === param.id
                );

                return (
                  <TextField
                    margin="dense"
                    label={parameterDefinition?.name ?? param.id}
                    key={param.id}
                    value={param.value}
                    fullWidth
                    disabled
                  />
                );
              })
            ) : (
              <Typography variant="body1">No parameters defined</Typography>
            )}
          </Stack>
          <Typography variant="h6">Batches</Typography>
          <Stack>
            {workflowInfo?.batches && (
              <TextField
                margin="dense"
                label="Batch Count"
                value={workflowInfo.batches}
                fullWidth
                disabled
              />
            )}
          </Stack>
        </Stack>
      </Box>
      <Box>{error !== null && <AsyncStatus error={error as any} />}</Box>
      <Stack direction="row" justifyContent="flex-end" spacing={2}>
        <TrackedButton
          trackLabel="form-cancel-export"
          disabled={isUpdating || isUpdatingWorkflow}
          color="secondary"
          variant="text"
          onClick={onClose}
        >
          Cancel
        </TrackedButton>
        <TrackedButton
          trackLabel="form-submit-export"
          loading={isUpdating || isUpdatingWorkflow}
          disabled={isUpdating || isUpdatingWorkflow}
          variant="contained"
          type="submit"
          onClick={handleWorkflowExport}
        >
          Export workflow
        </TrackedButton>
      </Stack>
    </Stack>
  );
};

interface ExportWorkflowFormProps {
  workflowID: string;
  onClose: () => void;
  refetchData: () => Promise<void>;
}

export const ExportWorkflowForm = ({
  workflowID,
  onClose,
  refetchData,
}: ExportWorkflowFormProps) => {
  const {
    data: workflowData,
    isLoading,
    isLoadingError,
  } = useGetWorkflowDetails({
    pathParams: {
      workflowID: workflowID,
    },
  });

  if (isLoading) {
    return <Placeholder copy="Getting data..." />;
  }

  if (isLoadingError) {
    return <Placeholder copy="Error loading workflow" />;
  }

  if (workflowData === undefined) {
    return <Placeholder copy="No workflow data available" />;
  }

  return (
    <FormWrapper
      workflow={workflowData}
      refetchData={refetchData}
      onClose={onClose}
    />
  );
};
